import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import {
  useAvailablePackedGoods,
  useCreateSalesOrder,
} from '@hooks/useSalesAndBilling'
import { useStores } from '@hooks/useProcurement'

const soItemSchema = z.object({
  packedGoodId: z.coerce.number().positive('Packed good is required'),
  qtyOrdered: z.coerce.number().positive('Quantity must be greater than 0'),
  ratePerUnit: z.coerce.number().positive('Rate must be greater than 0'),
})

const soSchema = z.object({
  storeId: z.coerce.number().positive('Store is required'),
  orderDate: z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date'),
  items: z.array(soItemSchema).min(1, 'At least one item is required'),
})

type SalesOrderFormData = z.infer<typeof soSchema>

export function SalesOrderFormPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const { data: storesData } = useStores()
  const { data: packedGoodsData } = useAvailablePackedGoods(search)
  const create = useCreateSalesOrder()

  const stores = storesData?.data?.items || []
  const packedGoods = packedGoodsData?.data || []

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SalesOrderFormData>({
    defaultValues: {
      storeId: 0,
      orderDate: new Date().toISOString().split('T')[0],
      items: [{ packedGoodId: 0, qtyOrdered: 0, ratePerUnit: 0 }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  })

  const selectedItems = watch('items')

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => {
      const qty = parseFloat(String(item.qtyOrdered)) || 0
      const rate = parseFloat(String(item.ratePerUnit)) || 0
      return sum + qty * rate
    }, 0)
  }

  const onSubmit = async (data: SalesOrderFormData) => {
    try {
      setError(null)
      const formattedData = {
        ...data,
        orderDate: new Date(data.orderDate).toISOString(),
      }

      const result = await create.mutateAsync(formattedData)
      navigate(`/dashboard/sales/orders/${result.data.id}`)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create sales order')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Sales Order</h1>
          <p className="text-gray-600 text-sm mt-1">
            Create an order for packed goods to store
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/sales/orders')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Order Header</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store *
              </label>
              <select {...register('storeId')} className="w-full">
                <option value="">Select Store</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.storeName}
                  </option>
                ))}
              </select>
              {errors.storeId && (
                <span className="text-red-500 text-sm">
                  {errors.storeId.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Date *
              </label>
              <input
                {...register('orderDate')}
                type="date"
                className="w-full"
              />
              {errors.orderDate && (
                <span className="text-red-500 text-sm">
                  {errors.orderDate.message}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Order Items</h2>
          </div>

          <div className="mb-4">
            <input
              type="text"
              placeholder="Search packed goods..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm"
            />
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-semibold">Packed Good</th>
                  <th className="text-right px-4 py-2 font-semibold">Available</th>
                  <th className="text-right px-4 py-2 font-semibold">Qty Ordered</th>
                  <th className="text-right px-4 py-2 font-semibold">Rate/Unit</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-center px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  const pg = packedGoods.find(
                    (p) => p.id === selectedItems[index]?.packedGoodId
                  )
                  const qty = parseFloat(String(selectedItems[index]?.qtyOrdered)) || 0
                  const rate = parseFloat(String(selectedItems[index]?.ratePerUnit)) || 0
                  const amount = qty * rate

                  return (
                    <tr key={field.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <select
                          {...register(`items.${index}.packedGoodId`, {
                            valueAsNumber: true,
                          })}
                          className="w-full text-sm"
                        >
                          <option value="">Select</option>
                          {packedGoods.map((pg) => (
                            <option key={pg.id} value={pg.id}>
                              {pg.repacking.bulkBatch.product.name} ({pg.packSize.size} {pg.packSize.uom})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right text-xs">
                        {pg?.availableQty.toFixed(0) || '-'}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.qtyOrdered`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          min="0"
                          step="1"
                          className="w-20 text-right text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.ratePerUnit`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-24 text-right text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        ₹{amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2">
                  <td colSpan={4} className="px-4 py-3 text-right font-semibold">
                    Total:
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-lg">
                    ₹{calculateTotal().toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <button
            type="button"
            onClick={() =>
              append({ packedGoodId: 0, qtyOrdered: 0, ratePerUnit: 0 })
            }
            className="btn-secondary text-sm"
          >
            + Add Item
          </button>

          {errors.items && (
            <span className="text-red-500 text-sm block mt-2">
              {errors.items.message}
            </span>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/sales/orders')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Sales Order'}
          </button>
        </div>
      </form>
    </div>
  )
}
